/** Read-time Canvas edge handle normalization for historical graphs.
 *
 * Repairs persisted edges whose `relation_type` is correct but
 * `source_handle` / `target_handle` is null, legacy, or mismatched so
 * React Flow can resolve real DOM `<Handle>` ids instead of falling
 * back to the node boundary. */

import type { CanvasEdgeDto, CanvasNodeDto } from './types'
import {
  GENERIC_INPUT_HANDLE,
  IMAGE_PROMPT_IN_HANDLE,
  VIDEO_ORIGINAL_PROMPT_IN_HANDLE,
  VIDEO_PROMPT_ENHANCEMENT_HANDLE,
  VIDEO_REFERENCE_VIDEO_HANDLE,
  imageReferenceAssetHandleId,
  isOriginalPromptInputHandle,
  isPromptEnhancementHandle,
  isVideoDialogueAudioHandle,
  isVideoKeyframeHandle,
  isVideoStructuredAssetHandle,
} from './dataflow'
import {
  PROMPT_ENHANCEMENT_RESULT_EDGE_RELATION,
  PROMPT_ENHANCEMENT_SOURCE_EDGE_RELATION,
  VIDEO_PROMPT_CONTEXT_IN_HANDLE,
  VIDEO_PROMPT_SOURCE_OUT_HANDLE,
} from './videoPromptEnhancement'
import { GENERATE_SCRIPT_SOURCE_INPUT_HANDLE } from './scriptSourceContext'
import { CREATIVE_CONTEXT_EDGE_RELATION } from './scriptGenerationCanvas'

/** Canonical aliases (same persisted wire ids — never a second naming scheme). */
export const REFERENCE_IMAGE_INPUT_HANDLE = GENERIC_INPUT_HANDLE
export const PROMPT_INPUT_HANDLE = VIDEO_PROMPT_ENHANCEMENT_HANDLE

const LEGACY_NULLISH_HANDLES = new Set(['', 'in', 'generic-in', 'input', 'out', 'output'])

const LEGACY_REFERENCE_TARGET_HANDLES = new Set([
  ...LEGACY_NULLISH_HANDLES,
  'reference-in',
  'reference-image-in',
  'image-in',
])

const LEGACY_PROMPT_TARGET_HANDLES = new Set([
  ...LEGACY_NULLISH_HANDLES,
  'prompt-in',
  'text-in',
  GENERIC_INPUT_HANDLE,
])

const IMAGE_SOURCE_TYPES = new Set(['image_asset', 'generate_image'])
const TEXT_SOURCE_TYPES = new Set(['generate_text', 'text_asset'])
const SCRIPT_SOURCE_TYPES = new Set(['generate_creative', 'creative_candidate', 'text_asset'])

function isBlankHandle(handle: string | null | undefined): boolean {
  return handle == null || LEGACY_NULLISH_HANDLES.has(handle)
}

function nodeTypeById(nodes: CanvasNodeDto[], nodeId: string): string | null {
  const node = nodes.find((n) => n.id === nodeId)
  return node?.node_type ?? null
}

function isProtectedGenerateVideoTargetHandle(handle: string | null | undefined): boolean {
  if (!handle) return false
  return (
    handle === GENERIC_INPUT_HANDLE ||
    handle === VIDEO_REFERENCE_VIDEO_HANDLE ||
    isOriginalPromptInputHandle(handle) ||
    isPromptEnhancementHandle(handle) ||
    isVideoKeyframeHandle(handle) ||
    isVideoDialogueAudioHandle(handle) ||
    isVideoStructuredAssetHandle(handle)
  )
}

function normalizeGenerateVideoTargetHandle(
  dto: CanvasEdgeDto,
  sourceType: string | null,
): string | null {
  const relation = dto.relation_type
  const current = dto.target_handle

  if (isProtectedGenerateVideoTargetHandle(current)) {
    if (relation === PROMPT_ENHANCEMENT_RESULT_EDGE_RELATION && current !== VIDEO_PROMPT_ENHANCEMENT_HANDLE) {
      return VIDEO_PROMPT_ENHANCEMENT_HANDLE
    }
    // Structured asset / reference-video / keyframe handles keep their
    // persisted semantic id — never downgrade to generic image-ref-in.
    if (
      isVideoStructuredAssetHandle(current) ||
      current === VIDEO_REFERENCE_VIDEO_HANDLE ||
      isVideoKeyframeHandle(current)
    ) {
      return current
    }
    return current
  }

  if (relation === PROMPT_ENHANCEMENT_RESULT_EDGE_RELATION) {
    return VIDEO_PROMPT_ENHANCEMENT_HANDLE
  }

  // `relation` can never be PROMPT_ENHANCEMENT_RESULT_EDGE_RELATION here —
  // the check above already returned for that case, so only the
  // ORIGINAL_PROMPT_IN branch is reachable (kept in sync with dead-code
  // removal to satisfy TS2367 no-overlap; runtime behavior unchanged).
  if (sourceType && TEXT_SOURCE_TYPES.has(sourceType)) {
    if (isBlankHandle(current) || LEGACY_PROMPT_TARGET_HANDLES.has(current ?? '')) {
      return VIDEO_ORIGINAL_PROMPT_IN_HANDLE
    }
  }

  if (relation === 'REFERENCES' || relation === 'INPUT_TO') {
    if (sourceType && IMAGE_SOURCE_TYPES.has(sourceType)) {
      if (isBlankHandle(current) || LEGACY_REFERENCE_TARGET_HANDLES.has(current ?? '')) {
        return GENERIC_INPUT_HANDLE
      }
    }
  }

  if (isBlankHandle(current)) {
    // Same unreachable-PROMPT_ENHANCEMENT_RESULT reasoning as above.
    if (sourceType && TEXT_SOURCE_TYPES.has(sourceType)) {
      return VIDEO_ORIGINAL_PROMPT_IN_HANDLE
    }
    if (sourceType && IMAGE_SOURCE_TYPES.has(sourceType)) return GENERIC_INPUT_HANDLE
  }

  return current
}

function normalizeSourceHandle(dto: CanvasEdgeDto, sourceType: string | null): string | null {
  const current = dto.source_handle
  if (sourceType && IMAGE_SOURCE_TYPES.has(sourceType)) {
    if (isBlankHandle(current) || current === 'image') return 'image-out'
  }
  if (sourceType && TEXT_SOURCE_TYPES.has(sourceType)) {
    if (isBlankHandle(current) || current === 'text') return 'text-out'
  }
  if (relationIsPromptEnhancementSource(dto) && sourceType === 'generate_video') {
    if (current !== VIDEO_PROMPT_SOURCE_OUT_HANDLE) return VIDEO_PROMPT_SOURCE_OUT_HANDLE
  }
  if (dto.relation_type === PROMPT_ENHANCEMENT_RESULT_EDGE_RELATION && sourceType === 'generate_text') {
    if (isBlankHandle(current)) return 'text-out'
  }
  return current
}

function relationIsPromptEnhancementSource(dto: CanvasEdgeDto): boolean {
  return dto.relation_type === PROMPT_ENHANCEMENT_SOURCE_EDGE_RELATION
}

function normalizeGenerateScriptTargetHandle(dto: CanvasEdgeDto, sourceType: string | null): string | null {
  const relation = dto.relation_type
  const current = dto.target_handle
  const isScriptSourceEdge =
    relation === CREATIVE_CONTEXT_EDGE_RELATION ||
    (relation === 'INPUT_TO' && sourceType != null && SCRIPT_SOURCE_TYPES.has(sourceType))
  if (!isScriptSourceEdge) return current
  if (current === GENERATE_SCRIPT_SOURCE_INPUT_HANDLE) return current
  if (isBlankHandle(current) || current === GENERIC_INPUT_HANDLE || current === 'image-ref-in') {
    return GENERATE_SCRIPT_SOURCE_INPUT_HANDLE
  }
  return current
}

function normalizeGenerateTextTargetHandle(dto: CanvasEdgeDto, sourceType: string | null): string | null {
  const current = dto.target_handle
  if (sourceType && IMAGE_SOURCE_TYPES.has(sourceType)) {
    if (isBlankHandle(current) || current === GENERIC_INPUT_HANDLE || LEGACY_REFERENCE_TARGET_HANDLES.has(current ?? '')) {
      return imageReferenceAssetHandleId(dto.id)
    }
  }
  return current
}

function normalizeGenerateImageTargetHandle(dto: CanvasEdgeDto, sourceType: string | null): string | null {
  const current = dto.target_handle
  if (sourceType && TEXT_SOURCE_TYPES.has(sourceType)) {
    if (isBlankHandle(current) || current === GENERIC_INPUT_HANDLE || LEGACY_PROMPT_TARGET_HANDLES.has(current ?? '')) {
      return IMAGE_PROMPT_IN_HANDLE
    }
    return current
  }
  // VistoeraTASK-00144 — a reference-image edge (image_asset/generate_image
  // source) already carrying its own `::<edgeId>`-scoped handle falls
  // through every check below unchanged (none of them match a scoped
  // string); any bare/legacy id gets assigned THIS edge's OWN stable id
  // as its scope exactly once — idempotent across reloads since
  // `dto.id` never changes, and never touches the edge's source/target/
  // relation, so EDGE_COUNT and the canonical binding are unaffected
  // (spec §4/§5).
  if (sourceType && IMAGE_SOURCE_TYPES.has(sourceType)) {
    if (isBlankHandle(current) || current === GENERIC_INPUT_HANDLE || LEGACY_REFERENCE_TARGET_HANDLES.has(current ?? '')) {
      return imageReferenceAssetHandleId(dto.id)
    }
  }
  return current
}

export interface NormalizeCanvasEdgeHandlesResult {
  edges: CanvasEdgeDto[]
  changed: boolean
  repairs: Array<{ edgeId: string; field: 'source_handle' | 'target_handle'; from: string | null; to: string | null }>
}

/** Normalize persisted edge handles once on Canvas hydrate (read-time only). */
export function normalizeHydratedCanvasEdgeDtos(
  nodes: CanvasNodeDto[],
  edges: CanvasEdgeDto[],
): NormalizeCanvasEdgeHandlesResult {
  const repairs: NormalizeCanvasEdgeHandlesResult['repairs'] = []
  const normalized = edges.map((edge) => {
    const sourceType = nodeTypeById(nodes, edge.source_node_id)
    const targetType = nodeTypeById(nodes, edge.target_node_id)
    let source_handle = edge.source_handle
    let target_handle = edge.target_handle

    source_handle = normalizeSourceHandle(edge, sourceType)
    if (targetType === 'generate_video') {
      target_handle = normalizeGenerateVideoTargetHandle(edge, sourceType)
    } else if (targetType === 'generate_image') {
      target_handle = normalizeGenerateImageTargetHandle(edge, sourceType)
    } else if (targetType === 'generate_text') {
      target_handle = normalizeGenerateTextTargetHandle(edge, sourceType)
    } else if (targetType === 'script_generation') {
      target_handle = normalizeGenerateScriptTargetHandle(edge, sourceType)
      if (sourceType === 'text_asset' && isBlankHandle(source_handle)) {
        source_handle = 'text-out'
      }
    } else if (
      edge.relation_type === PROMPT_ENHANCEMENT_SOURCE_EDGE_RELATION &&
      sourceType === 'generate_video' &&
      targetType === 'generate_text'
    ) {
      if (target_handle !== VIDEO_PROMPT_CONTEXT_IN_HANDLE) {
        target_handle = VIDEO_PROMPT_CONTEXT_IN_HANDLE
      }
    }

    if (source_handle !== edge.source_handle) {
      repairs.push({
        edgeId: edge.id,
        field: 'source_handle',
        from: edge.source_handle,
        to: source_handle,
      })
    }
    if (target_handle !== edge.target_handle) {
      repairs.push({
        edgeId: edge.id,
        field: 'target_handle',
        from: edge.target_handle,
        to: target_handle,
      })
    }

    if (source_handle === edge.source_handle && target_handle === edge.target_handle) return edge
    return { ...edge, source_handle, target_handle }
  })

  return { edges: normalized, changed: repairs.length > 0, repairs }
}

export interface FlowEdgeHandleFields {
  sourceHandle?: string | null
  targetHandle?: string | null
  data?: { relationType?: string }
}

export function isPromptEnhancementSourceRelationEdge(edge: FlowEdgeHandleFields): boolean {
  return (edge.data as { relationType?: string } | undefined)?.relationType === PROMPT_ENHANCEMENT_SOURCE_EDGE_RELATION
}

/** Runtime React Flow edge rebind — mirrors DTO hydration for PROMPT_ENHANCEMENT_SOURCE only. */
export function normalizePromptEnhancementSourceFlowEdge<T extends FlowEdgeHandleFields>(edge: T): T {
  if (!isPromptEnhancementSourceRelationEdge(edge)) return edge
  const nextSource = VIDEO_PROMPT_SOURCE_OUT_HANDLE
  const nextTarget = VIDEO_PROMPT_CONTEXT_IN_HANDLE
  if (edge.sourceHandle === nextSource && edge.targetHandle === nextTarget) return edge
  return { ...edge, sourceHandle: nextSource, targetHandle: nextTarget }
}

export function normalizeFlowEdgesPromptEnhancementSource<T extends FlowEdgeHandleFields>(edges: T[]): T[] {
  let changed = false
  const next = edges.map((edge) => {
    const normalized = normalizePromptEnhancementSourceFlowEdge(edge)
    if (normalized !== edge) changed = true
    return normalized
  })
  return changed ? next : edges
}
